import { Injectable } from '@angular/core';
import { EntityQuery } from 'breeze-client';
import { uniqueArrayFromPropertyPath } from '@common/util';
import { DataManagerService } from '@services/data-manager.service';
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { MaterialNicknameOnJobHistory } from '../common/types/models/material-nickname-on-job-history.interface';

export interface GenerateAnimalJobMaterialNicknamesRequest {
    JobKey: number,
    Prefix: string,
    Suffix: string,
    Counter: number,
    RemovedJobMaterialKeys: number[],
    // Job material keys to generate nicknames for. If not specified, nicknames are generated for all job materials for the job specified.
    JobMaterialKeys: number[]
}

export interface GetAnimalJobMaterialNicknamesRequest {
    JobKey: number,
    // Current nicknames with job material keys as property names and current nicknames as values
    CurrentNicknames: Record<number, string>
}

export interface NicknameData {
    Nickname: string | null;
    NicknameSortable: string | null;
}

@Injectable()
export class NicknameDataAccessService {

    constructor(
        private httpClient: HttpClient,
        private dataManager: DataManagerService
    ) {
    }

    /**
     * Returns current animal nicknames on jobs.
     * @returns
     */
    async getCurrentAnimalNicknamesOnJobs(): Promise<string[]> {
        const query = EntityQuery
            .from('MaterialNicknamesOnJobHistory')
            .where('IsCurrent', '==', 'true')
            .where('Nickname', "!=", null)
            .where('Material.cv_MaterialType.MaterialType', '==', 'Animal')
            .select('Nickname')
            .orderBy('NicknameSortable');

        const nicknames = await this.dataManager.returnQueryResults(query);

        return uniqueArrayFromPropertyPath(nicknames, 'Nickname');
    }

    /**
     * Returns unique current and past animal nicknames on jobs.
     */
    async getCurrentAndPastAnimalNicknamesOnJobs(): Promise<string[]> {
        const query = EntityQuery
            .from('MaterialNicknamesOnJobHistory')
            .where('Nickname', "!=", null)
            .where('Material.cv_MaterialType.MaterialType', '==', 'Animal')
            .select('Nickname')
            .orderBy('NicknameSortable');

        const nicknames = await this.dataManager.returnQueryResults(query);

        return uniqueArrayFromPropertyPath(nicknames, 'Nickname');
    }

    /**
     * Returns current and past MaterialNicknameOnJobHistory on jobs by job keys
     */
    getMaterialNicknamesOnJobHistoryByJobKeys(jobKeys: number[]): Promise<MaterialNicknameOnJobHistory[]> {
        const query = EntityQuery
            .from('MaterialNicknamesOnJobHistory')
            .where('C_Job_key', 'in', jobKeys)
            .where('Material.cv_MaterialType.MaterialType', '==', 'Animal')
            .orderBy('DateModified DESC');

        return this.dataManager.returnQueryResults(query);
    }

    /**
     * Generates new nicknames for animal job materials based on prefix, counter, and sufix.
     */
    generateAnimalJobMaterialNicknames(data: GenerateAnimalJobMaterialNicknamesRequest): Promise<Record<number, NicknameData>> {
        return this.httpClient.post(
            "/api/jobdata/generateNicknameOnJob",
            data,
            { headers: new HttpHeaders() }
        ).toPromise() as unknown as Promise<Record<number, NicknameData>>;
    }

    /**
     * Renegerates sortable nicknames for specified animal job materials and
     * appends the nicknames from server for not specified animal job materials.
     */
    getAnimalJobMaterialNicknames(data: GetAnimalJobMaterialNicknamesRequest): Promise<Record<number, NicknameData>> {
        return this.httpClient.post(
            "/api/jobdata/getNicknameSortableOnJob",
            data,
            { headers: new HttpHeaders() }
        ).toPromise() as unknown as Promise<Record<number, NicknameData>>;
    }
}
